import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { t } from 'i18next';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import TreadLiveMapV2 from '~components/Maps/v2/TreadLiveMapV2';
import OrderAuditLog from '~components/Order/AuditLog/OrderAuditLog';
import LoadsTabContent from '~components/Order/LoadsTab/LoadsTabContent';
import { Order } from '~hooks/useOrders/models';
import FilesPageContext from '~pages/Approvals/Files/FilesPageContext';
import {
  DefaultSelectedTab,
  getStoredDefaultTab,
} from '~pages/Approvals/Files/FilesPageContext';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import { makeLoadsJobFilters } from '~pages/Approvals/Files/loadsJobFilters';
import { OrderDetails } from '~pages/Dispatch/OrderDetails';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ExpandedBlockTabs } from '../../styled';
import { JobsQueryContextProvider } from './JobsQueryContext';
import JobsTab from './JobsTab';

enum ExpandedBlockTab {
  JOBS = 'jobs',
  LOADS = 'loads',
  ORDER_DETAILS = 'order_details',
  LIVE_MAP = 'live_map',
  TIMELINE = 'timeline',
}

const tabs = [
  { key: ExpandedBlockTab.JOBS, label: t('form_fields.jobs') },
  { key: ExpandedBlockTab.LOADS, label: t('common.loads') },
  { key: ExpandedBlockTab.ORDER_DETAILS, label: t('order.form.order_details') },
  { key: ExpandedBlockTab.LIVE_MAP, label: t('live_map.live_map') },
  { key: ExpandedBlockTab.TIMELINE, label: t('dispatch.timeline.timeline') },
];

interface ExpandedBlockProps {
  order: Order;
}

export default memo(function ExpandedBlock({ order }: ExpandedBlockProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { defaultSelectedTab } = useContext(FilesPageContext);

  const [selectedTab, setSelectedTab] = useState(() =>
    getStoredDefaultTab() === DefaultSelectedTab.LOADS
      ? ExpandedBlockTab.LOADS
      : ExpandedBlockTab.JOBS,
  );

  const filesQueryContext = useContext(FilesQueryContext);
  const loadsFilters = useMemo(
    () => makeLoadsJobFilters(filesQueryContext.filters),
    [filesQueryContext.filters],
  );

  const handleSelectedTabChange = useCallback(
    (__: React.SyntheticEvent, value: ExpandedBlockTab) => {
      setSelectedTab(value);
    },
    [],
  );

  useEffect(() => {
    setSelectedTab(
      defaultSelectedTab === DefaultSelectedTab.LOADS
        ? ExpandedBlockTab.LOADS
        : ExpandedBlockTab.JOBS,
    );
  }, [defaultSelectedTab]);

  return (
    <Box>
      <Box sx={{ px: 1 }}>
        <ExpandedBlockTabs value={selectedTab} onChange={handleSelectedTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </ExpandedBlockTabs>
      </Box>

      <JobsQueryContextProvider
        order={order}
        companyId={companyId}
        shouldFetchOnMount={defaultSelectedTab === DefaultSelectedTab.LOADS}
      >
        {selectedTab === ExpandedBlockTab.JOBS && <JobsTab />}

        {selectedTab === ExpandedBlockTab.LOADS && (
          <LoadsTabContent filters={loadsFilters} order={order} />
        )}

        {selectedTab === ExpandedBlockTab.ORDER_DETAILS && (
          <Box sx={{ p: 1 }}>
            <OrderDetails order={order} />
          </Box>
        )}

        {selectedTab === ExpandedBlockTab.LIVE_MAP && (
          <Box
            sx={(theme) => ({
              bgcolor: theme.palette.background.paper,
              border: 1,
              borderColor: theme.brandV2.colors.treadGray7,
              borderRadius: 1,
              height: 400,
              m: 1,
              overflow: 'hidden',
            })}
          >
            <TreadLiveMapV2 order={order} />
          </Box>
        )}

        {selectedTab === ExpandedBlockTab.TIMELINE && <OrderAuditLog order={order} />}
      </JobsQueryContextProvider>
    </Box>
  );
});
